'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface GlobalErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

/**
 * Global Next.js Error Boundary.
 * Catches errors that bubble up from all routes.
 */
export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // Log error - we use console.error here since logError might not be available
    // in the error boundary context
    console.error('[GlobalError]', error.message, { digest: error.digest })
    Sentry.captureException(error)
  }, [error])

  return (
    <html>
      <body>
        <div className="flex min-h-screen items-center justify-center p-4 bg-background">
          <Card className="mx-auto max-w-lg w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle>Application Error</CardTitle>
              <CardDescription>
                An unexpected error occurred. Please try again or contact support if the problem
                persists.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {process.env.NODE_ENV === 'development' && (
                <details className="rounded-md border bg-gray-50 p-3 text-xs">
                  <summary className="cursor-pointer font-medium text-gray-600">
                    Error details (development only)
                  </summary>
                  <pre className="mt-2 overflow-auto whitespace-pre-wrap text-red-600">
                    {error.message}
                  </pre>
                  {error.stack && (
                    <pre className="mt-2 overflow-auto whitespace-pre-wrap text-gray-500">
                      {error.stack}
                    </pre>
                  )}
                </details>
              )}
              <div className="flex justify-center gap-2">
                <Button onClick={reset} className="bg-primary hover:bg-primary/90">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
                <Button
                  onClick={() => (window.location.href = '/')}
                  variant="outline"
                  className="border-gray-300"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Go Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </body>
    </html>
  )
}
